import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QrCode, X, Calendar, Trash2, Barcode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Read the cached booking-scoped bearer token for a phone, if still valid. */
function readToken(phone) {
  try {
    const raw = localStorage.getItem(`eif_bt_${phone}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p.token || !p.expires_at) return null;
    if (new Date(p.expires_at) < new Date()) return null;
    return p.token;
  } catch (_) { return null; }
}
function saveToken(phone, data) {
  if (data?.booking_token) {
    localStorage.setItem(`eif_bt_${phone}`, JSON.stringify({
      token: data.booking_token, expires_at: data.expires_at, phone,
    }));
  }
}
function clearToken(phone) { localStorage.removeItem(`eif_bt_${phone}`); }

export default function MyBookingsPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [phone, setPhone] = React.useState(localStorage.getItem("eif_phone") || "");
  const [bookings, setBookings] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [qrModal, setQrModal] = React.useState(null);
  const [ticketTab, setTicketTab] = React.useState("qr");
  // OTP verification state
  const [needsOtp, setNeedsOtp] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const [demoOtp, setDemoOtp] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const bearerCfg = (p) => {
    const tok = readToken(p);
    return tok ? { headers: { Authorization: `Bearer ${tok}` } } : null;
  };

  const fetchBookings = async (p) => {
    const cfg = bearerCfg(p);
    if (!cfg) return false;
    try {
      const res = await axios.get(`${API}/bookings`, cfg);
      setBookings(res.data);
      setLoaded(true);
      setNeedsOtp(false);
      localStorage.setItem("eif_phone", p);
      return true;
    } catch (e) {
      if (e.response?.status === 401) { clearToken(p); return false; }
      toast.error(e.response?.data?.detail || "Failed to load");
      return false;
    }
  };

  const load = async () => {
    if (!phone.trim()) return;
    const ok = await fetchBookings(phone);
    if (!ok) {
      // Need OTP verification to mint a token
      setNeedsOtp(true);
      setBookings([]); setLoaded(false);
    }
  };

  const requestOtp = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/otp/send`, { phone });
      setDemoOtp(res.data.demo_otp);
      toast.success("OTP sent (demo)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OTP send failed");
    } finally { setBusy(false); }
  };

  const verifyAndLoad = async () => {
    if (!otp) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/bookings/session?otp=${encodeURIComponent(otp)}`, { phone });
      saveToken(phone, res.data);
      setOtp(""); setDemoOtp(null);
      await fetchBookings(phone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setBusy(false); }
  };

  const cancel = async (b) => {
    const cfg = bearerCfg(phone);
    if (!cfg) { setNeedsOtp(true); return; }
    try {
      await axios.delete(`${API}/bookings/${b.id}`, cfg);
      toast.success("Cancelled");
      await fetchBookings(phone);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(phone); setNeedsOtp(true); }
      else toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const gcalLink = (b) => {
    const s = b.session;
    const fmt = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const title = encodeURIComponent(s.title?.[lng] || s.title?.en);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(s.starts_at)}/${fmt(s.ends_at)}&location=${encodeURIComponent(s.room)}&details=89th+ACHPR+NGO+Forum+Dakar+Side+Events+2026`;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="tag-mono mb-2">{t("nav.myBookings")}</div>
      <h1 className="font-serif-diplo text-3xl sm:text-4xl font-bold text-[color:var(--navy)]">{t("bookings.title")}</h1>
      <div className="gold-underline mt-4 mb-8" />

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          data-testid="bookings-phone-input"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); setNeedsOtp(false); }}
          placeholder={t("bookings.loadPrompt")}
          className="flex-1 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--gold)]"
        />
        <button data-testid="bookings-load-btn" onClick={load} className="btn-navy px-5 py-2.5 rounded-lg text-sm font-semibold">
          {t("bookings.load")}
        </button>
      </div>

      {needsOtp && (
        <div className="diplomatic-card p-4 space-y-3 mb-6" data-testid="bookings-otp-panel">
          <div className="tag-mono">Verify WhatsApp OTP to view your bookings</div>
          {demoOtp && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <span className="font-mono font-semibold">DEMO OTP: {demoOtp}</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button data-testid="bookings-otp-send" onClick={requestOtp} disabled={busy}
                    className="btn-navy px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
              {demoOtp ? "Resend OTP" : "Send OTP"}
            </button>
            <input data-testid="bookings-otp-input" inputMode="numeric" maxLength={6}
                   value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                   placeholder="6-digit code"
                   className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm font-mono-diplo tracking-widest text-center" />
            <button data-testid="bookings-otp-verify" onClick={verifyAndLoad} disabled={busy || !otp}
                    className="btn-gold px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
              Verify & Load
            </button>
          </div>
        </div>
      )}

      {loaded && bookings.length === 0 && (
        <div className="text-center text-slate-400 font-mono-diplo text-xs py-16">{t("bookings.empty")}</div>
      )}

      <div className="space-y-3">
        {bookings.map((b) => (
          <div key={b.id} className="diplomatic-card p-5" data-testid={`booking-row-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="tag-mono">{t(`filters.${b.session?.type}`)} · {t("day")} {b.session?.day}</div>
                <div className="font-serif-diplo text-lg text-[color:var(--navy)] mt-1">
                  {b.session?.title?.[lng] || b.session?.title?.en}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {new Date(b.session?.starts_at).toLocaleString(lng, { dateStyle: "medium", timeStyle: "short" })} · {b.session?.room}
                </div>
                <div className="mt-2">
                  {b.status === "confirmed" ? (
                    <span className="text-xs text-emerald-700 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">{t("bookings.confirmed")}</span>
                  ) : (
                    <span className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                      {t("bookings.waitlisted", { n: b.waitlist_position })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {b.status === "confirmed" && (
                  <>
                    <button data-testid={`qr-btn-${b.id}`} onClick={() => { setTicketTab("qr"); setQrModal(b); }}
                      className="btn-navy px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5" /> {t("bookings.qr")}
                    </button>
                    <button data-testid={`barcode-btn-${b.id}`} onClick={() => { setTicketTab("barcode"); setQrModal(b); }}
                      className="px-3 py-2 rounded-lg text-xs border border-slate-200 inline-flex items-center gap-1.5">
                      <Barcode className="w-3.5 h-3.5" /> Barcode
                    </button>
                  </>
                )}
                <a data-testid={`ics-btn-${b.id}`} href={`${API}/bookings/${b.id}/ics`}
                  className="px-3 py-2 rounded-lg text-xs border border-slate-200 inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> {t("bookings.exportIcs")}
                </a>
                <a data-testid={`gcal-btn-${b.id}`} target="_blank" rel="noreferrer" href={gcalLink(b)}
                  className="px-3 py-2 rounded-lg text-xs border border-slate-200 inline-flex items-center gap-1.5">
                  {t("bookings.exportGoogle")}
                </a>
                <button data-testid={`cancel-btn-${b.id}`} onClick={() => cancel(b)}
                  className="px-3 py-2 rounded-lg text-xs border border-rose-200 text-rose-700 inline-flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> {t("bookings.cancel")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" onClick={() => setQrModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="tag-mono">EventHub · Dakar 2026</div>
              <button onClick={() => setQrModal(null)} data-testid="qr-modal-close"><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            <div className="font-serif-diplo text-lg text-[color:var(--navy)] mb-1">
              {qrModal.session?.title?.[lng] || qrModal.session?.title?.en}
            </div>
            <div className="text-xs text-slate-500 mb-4">{qrModal.name}</div>

            <div className="inline-flex rounded-full bg-slate-100 p-1 mb-4" role="tablist" data-testid="ticket-tabs">
              <button
                data-testid="ticket-tab-qr"
                data-active={ticketTab === "qr"}
                onClick={() => setTicketTab("qr")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${ticketTab === "qr" ? "bg-white shadow text-[color:var(--forest-dark)]" : "text-slate-500"}`}
              >
                <QrCode className="w-3.5 h-3.5" /> QR
              </button>
              <button
                data-testid="ticket-tab-barcode"
                data-active={ticketTab === "barcode"}
                onClick={() => setTicketTab("barcode")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${ticketTab === "barcode" ? "bg-white shadow text-[color:var(--forest-dark)]" : "text-slate-500"}`}
              >
                <Barcode className="w-3.5 h-3.5" /> Barcode
              </button>
            </div>

            {ticketTab === "qr" ? (
              <img data-testid="qr-image" src={`${API}/bookings/${qrModal.id}/qr`} alt="QR" className="w-full max-w-[240px] mx-auto" />
            ) : (
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <img data-testid="barcode-image" src={`${API}/bookings/${qrModal.id}/barcode`} alt="Barcode" className="w-full max-w-[360px] mx-auto" />
              </div>
            )}
            <div className="mt-3 font-mono-diplo text-[10px] text-slate-400 break-all">{qrModal.qr_token}</div>
            <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
              {ticketTab === "qr"
                ? "Show this QR to the door scanner. Any smartphone camera will read it."
                : "Hold your phone 15–20 cm from the scanner. Keep it steady, screen at full brightness."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
